// @TODO[WORK-5920]: this file bridges moment <-> dayjs for the antd v4 picker
// wrappers. Remove it once antd is upgraded to v5+ (which uses dayjs natively)
// and the wrappers no longer need to translate between the two.
// https://app.asana.com/1/303134204339906/project/1213739174531764/task/1214776614196739

/* eslint-disable no-restricted-imports -- this is the single canonical moment island; the antd v4 picker wrappers under @common/components are the only consumers of these helpers. When Task 10 enables the global moment rule, this directive future-proofs the file without further edits. */
import moment, { type Moment } from 'moment';

import { datetime, parseZone, type DatetimeType } from './datetime';

// Round-trip via the ISO 8601 string with offset suffix (`.format()`) so the
// display offset is preserved. `.toDate()` / `.toISOString()` would normalize
// to UTC and silently corrupt parseZone-style values used in shift configs.
//
// Null and undefined are intentionally distinguished — in React's controlled-
// component contract they mean different things (`null` = "controlled, no
// current value"; `undefined` = "uncontrolled, use internal state"). Collapsing
// them would force every antd picker wrapper to be controlled the moment a
// caller mounts it without a `value` prop, breaking selection persistence.

// Overloads are ordered so TypeScript can pick the narrowest return type for
// each call-site input. The union overloads at the bottom cover the most
// general cases and are what the implementation signature relies on.

export function dayjsToMoment(value: DatetimeType): Moment;
export function dayjsToMoment(value: null): null;
export function dayjsToMoment(value: undefined): undefined;
export function dayjsToMoment(value: DatetimeType | null): Moment | null;
export function dayjsToMoment(
  value: DatetimeType | undefined
): Moment | undefined;
export function dayjsToMoment(
  value: DatetimeType | null | undefined
): Moment | null | undefined;
export function dayjsToMoment(
  value: DatetimeType | null | undefined
): Moment | null | undefined {
  if (value === null) return null;
  if (value === undefined) return undefined;
  return moment.parseZone(value.format());
}

export function momentToDayjs(value: Moment): DatetimeType;
export function momentToDayjs(value: null): null;
export function momentToDayjs(value: undefined): undefined;
export function momentToDayjs(value: Moment | null): DatetimeType | null;
export function momentToDayjs(
  value: Moment | undefined
): DatetimeType | undefined;
export function momentToDayjs(
  value: Moment | null | undefined
): DatetimeType | null | undefined;
export function momentToDayjs(
  value: Moment | null | undefined
): DatetimeType | null | undefined {
  if (value === null) return null;
  if (value === undefined) return undefined;
  // A local-mode moment becomes a zone-aware (local) dayjs from its instant so a
  // later DST-crossing op recomputes the offset like moment does; parseZone(
  // value.format()) would freeze the current offset and diverge under month/day
  // arithmetic (WORK-6090, WORKS-340/341 — the same defect fixed in
  // createDatetime). UTC- and fixed-offset moments keep the offset-preserving
  // round-trip, so the picker bridge's parseZone-origin values are untouched.
  if (value.isLocal()) {
    return datetime(value.valueOf());
  }
  return parseZone(value.format());
}

// Forwards a locale-overrides payload to moment.updateLocale so consumers
// that customize antd Calendar's locale (today: shifts-calendar) don't need
// to import moment themselves. Mirrors the moment.updateLocale signature.
// Type is loose because moment's LocaleSpecification surface is broad and the
// wrapper is meant to be transparent to callers.
export function setMomentLocale(
  name: string,
  values: Record<string, unknown>
): void {
  moment.updateLocale(name, values);
}

// Re-export Moment as a type-only alias so picker wrappers can refer to it
// without importing moment directly (keeps the moment island confined to
// this single file).
export type { Moment };
